/**
 * Thin wrappers around in-meeting Zoom Apps SDK actions used by the
 * facilitator view. Each capability used here must also be listed in
 * REQUIRED_APIS (useZoomContext) and enabled in the Marketplace app config.
 */

import zoomSdk from '@zoom/appssdk';

/**
 * Public base URL of the participant-facing web app (for join links).
 * Override per-environment with VITE_WEB_BASE_URL (e.g. a branded domain once
 * the web app is served there); the default is the current Railway web URL.
 */
const WEB_BASE_URL =
  import.meta.env.VITE_WEB_BASE_URL ||
  'https://robust-dedication-production-f27f.up.railway.app';

/** Public base URL of the web app (no trailing slash). */
export function webBaseUrl(): string {
  return WEB_BASE_URL.replace(/\/$/, '');
}

/** Build the participant join URL for a session: <web>/join/<sessionId>. */
export function buildJoinUrl(sessionId: string): string {
  return `${WEB_BASE_URL.replace(/\/$/, '')}/join/${sessionId}`;
}

/**
 * Build the display-page URL for a session: <web>/display/<sessionId>.
 * Embedded in the collaborate view so participants see live results, not the
 * facilitator control panel.
 *
 * 2026-07: the display surfaces were unified — SessionDisplayPage was retired
 * and /session/:id/display now just redirects to the canonical /display/:id
 * (DisplayPage), whose live-advance bug was fixed earlier (PR #40). Point
 * straight at the canonical URL to skip the redirect hop inside the iframe.
 */
export function buildDisplayUrl(sessionId: string): string {
  return `${WEB_BASE_URL.replace(/\/$/, '')}/display/${sessionId}`;
}

/**
 * Post the session join link into the meeting chat so participants can tap in.
 * Host-only, in-meeting. Throws if the SDK rejects (e.g. capability missing).
 */
export async function shareJoinLinkToChat(sessionId: string): Promise<void> {
  const joinUrl = buildJoinUrl(sessionId);
  await zoomSdk.sendMessageToChat({
    message: `Join the session to take part: ${joinUrl}`,
  });
}

/**
 * Invite every participant to open this app in their own sidebar, where they
 * can respond to activities directly. Host-only, in-meeting.
 */
export async function inviteAllToApp(): Promise<void> {
  await zoomSdk.sendAppInvitationToAllParticipants();
}

/**
 * Start Collaborate mode so every participant's own app instance opens in the
 * main window (runningContext 'inCollaborate' → ZoomCollaborateDisplay renders
 * the live /display page). Host/co-host only.
 *
 * shareScreen MUST be false. Per the SDK docs it defaults to true, which
 * *mirrors the host's app screen* (our facilitator control panel) to everyone
 * as a screen-share preview — the opposite of what we want. shareScreen:false
 * opts out of that mirror so participants get the collaborate display instead.
 */
export async function startSharing(): Promise<void> {
  await zoomSdk.startCollaborate({ shareScreen: false });
}

/** Stop Collaborate mode. */
export async function stopSharing(): Promise<void> {
  await zoomSdk.endCollaborate();
}

/**
 * Open a URL in the participant's default browser. Zoom's embedded webview
 * blocks window.open/target=_blank, so the collaborate-display fallback must
 * go through the SDK. Requires the 'openUrl' capability.
 */
export async function openExternal(url: string): Promise<void> {
  await zoomSdk.openUrl({ url });
}

/**
 * Nudge participants that a new activity is live (in-client notification).
 * Best-effort: callers should .catch — the capability may be disabled.
 */
export async function notifyActivityStarted(title: string): Promise<void> {
  await zoomSdk.showNotification({
    type: 'info',
    title: 'New activity',
    message: title,
  } as Parameters<typeof zoomSdk.showNotification>[0]);
}

/** Expand this app's sidebar panel (participant nudge on activity start). */
export async function expandSidebar(): Promise<void> {
  await zoomSdk.expandApp({ action: 'expand' });
}

/** Number of people currently in the meeting (host/co-host only). */
export async function getRosterCount(): Promise<number> {
  const res = await zoomSdk.getMeetingParticipants();
  return res.participants?.length ?? 0;
}

/**
 * Subscribe to roster changes; returns an unsubscribe fn. Re-fetches the
 * full roster on each change event (the event payload is a delta).
 */
export function onRosterChange(cb: (count: number) => void): () => void {
  const handler = () => {
    getRosterCount().then(cb).catch(() => {});
  };
  try {
    zoomSdk.addEventListener('onParticipantChange', handler);
  } catch {
    return () => {};
  }
  return () => {
    try {
      zoomSdk.removeEventListener('onParticipantChange', handler);
    } catch {
      // ignore
    }
  };
}
